import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Header as UIHeader, HeaderLogo, HeaderNav } from '@voilajsx/uikit/header';
import { useAuth } from '../../features/auth';
import { hasRole, route } from '../utils';
import type { NavigationItem } from '@voilajsx/uikit';
import {
  LayoutDashboard,
  User,
  LogOut,
  Shield,
  LogIn,
  UserPlus,
  GraduationCap,
  ListChecks,
  Home,
  Search,
} from 'lucide-react';
import dinosaurLogo from '../../dinosaur.png';

// Logo component - now clickable
const Logo: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <div
    className="flex items-center gap-2.5 cursor-pointer hover:opacity-80 transition-opacity"
    onClick={onClick}
  >
    <img
      src={dinosaurLogo}
      alt="AskDino Logo"
      className="w-10 h-10 object-contain"
    />
    <div>
      <h3 className="voila-brand-logo text-xl font-bold text-foreground">AskDino</h3>
      <p className="text-xs text-muted-foreground">by FirstAcademy</p>
    </div>
  </div>
);


// Unified Header Component that handles both authenticated and public states
export const Header: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  // Build navigation items based on authentication state
  const navigationItems: NavigationItem[] = [];

  // Public navigation items (always visible)
  navigationItems.push(
    { key: 'home', label: 'Home', href: '/', icon: Home },
    { key: 'careers', label: 'Explore Careers', href: '/explore/careers', icon: GraduationCap },
    { key: 'quiz', label: 'Take Quiz', href: '/quiz', icon: ListChecks },
    { key: 'search', label: 'Search', href: '/explore/universities', icon: Search }
  );

  if (isAuthenticated) {
    // Authenticated navigation
    navigationItems.push(
      { key: 'dashboard', label: 'Dashboard', href: route('/dashboard'), icon: LayoutDashboard },
      { key: 'profile', label: 'Profile', href: route('/profile'), icon: User }
    );

    // Add admin menu items based on role
    if (user && hasRole(user, ['admin.tenant', 'admin.org', 'admin.system'])) {
      navigationItems.push({
        key: 'admin',
        label: 'Admin',
        href: route('/admin'),
        icon: Shield
      });
    }

    // Always add logout for authenticated users
    navigationItems.push({
      key: 'logout',
      label: 'Sign Out',
      href: route('/logout'),
      icon: LogOut
    });
  } else {
    // Public navigation (login/signup)
    navigationItems.push(
      {
        key: 'login',
        label: 'Sign In',
        href: route('/auth/login'),
        icon: LogIn
      },
      {
        key: 'register',
        label: 'Sign Up',
        href: route('/auth/register'),
        icon: UserPlus
      }
    );
  }

  const handleNavigation = (href: string) => {
    navigate(href);
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  return (
    <UIHeader tone="clean" size="xl" position="sticky">
      <HeaderLogo>
        <Logo onClick={handleLogoClick} />
      </HeaderLogo>

      <HeaderNav
        navigation={navigationItems}
        currentPath={location.pathname}
        onNavigate={handleNavigation}
      />
    </UIHeader>
  );
};

// Export PublicHeader as alias for backward compatibility
export const PublicHeader = Header;